import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Cookies() {
  return (
    <>
      <Helmet>
        <title>Cookie Policy — AURA</title>
        <meta name="description" content="AURA Cookie Policy - How we use cookies and similar technologies." />
      </Helmet>
      
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to AURA
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Cookie Policy</h1>
          <p className="text-sm text-muted-foreground mb-12">Last updated: January 2025</p>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-serif mb-4">What Are Cookies?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and improve your experience. AURA uses cookies and similar technologies to provide, secure, and improve our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="p-6 rounded-xl bg-muted/50">
                  <h3 className="text-lg font-medium mb-2">Essential Cookies</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Required for the Service to function. These cookies enable core functionality like user authentication, security, and session management. You cannot opt out of these cookies.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm mt-3 space-y-1">
                    <li>Authentication tokens</li>
                    <li>Security tokens (CSRF protection)</li>
                    <li>Session identifiers</li>
                  </ul>
                </div>
                
                <div className="p-6 rounded-xl bg-muted/50">
                  <h3 className="text-lg font-medium mb-2">Preference Cookies</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Remember your settings and preferences to personalize your experience.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm mt-3 space-y-1">
                    <li>Theme preference (light/dark mode)</li>
                    <li>Language preferences</li>
                    <li>Notification settings</li>
                  </ul>
                </div>
                
                <div className="p-6 rounded-xl bg-muted/50">
                  <h3 className="text-lg font-medium mb-2">Analytics Cookies</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Help us understand how users interact with the Service to improve functionality and user experience.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm mt-3 space-y-1">
                    <li>Page views and navigation patterns</li>
                    <li>Feature usage statistics</li>
                    <li>Performance metrics</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Local Storage</h2>
              <p className="text-muted-foreground leading-relaxed">
                In addition to cookies, we use browser local storage to store:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li><strong>Theme preference:</strong> Your light/dark mode setting</li>
                <li><strong>Onboarding status:</strong> Whether you've completed the style quiz</li>
                <li><strong>Cache data:</strong> Temporary storage for faster loading</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Third-Party Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Some third-party services we use may set their own cookies:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Razorpay:</strong> Payment processing and fraud prevention</li>
                <li><strong>Supabase:</strong> Authentication and session management</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                These third parties have their own privacy and cookie policies. We encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Managing Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can control cookies through your browser settings:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Block all cookies:</strong> May prevent the Service from functioning properly</li>
                <li><strong>Block third-party cookies:</strong> May affect payment processing</li>
                <li><strong>Delete cookies:</strong> Will log you out and reset preferences</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Most browsers allow you to manage cookie preferences. Refer to your browser's help documentation for instructions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Do Not Track</h2>
              <p className="text-muted-foreground leading-relaxed">
                AURA does not currently respond to "Do Not Track" browser signals. We will update this policy if we implement DNT support in the future.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Updates to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about our use of cookies, contact us at:
              </p>
              <p className="text-muted-foreground mt-2">
                <strong>Email:</strong> privacy@aura.style
              </p>
            </section>
          </div>
          
          <div className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Built by{' '}
              <a 
                href="https://bwestudios.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="font-bold hover:text-foreground transition-colors"
              >
                BWE Studio
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
